import styled from 'styled-components';
import Form from 'react-bootstrap/Form';
import { Tooltip } from 'react-tooltip';
import { Drawer, theme } from 'antd';

import SidebarTemplate from './sidebarElements/SidebarTemplate';
import useKeyboardShortcut from '@hooks/useKeyboardShortcut.hook';
import { KeyboardShortcut } from '../SharedStyles';

import { useProject } from '@contexts/Project.context';
import { useProjectMode } from '@contexts/ProjectMode.context';
import { ANNOTATION_PHASE, ANNOTATION_TARGET, PROJECT_VIEW_MODE } from '@utils/constants';
import AnnotationObjectTree from './annotation/AnnotationObjectTree';

const FilterTab = styled.div`
	width: 100%;
	text-align: left;
	background-color: ${props => props.theme.colors.modal};
	padding: 20px;
`;

const LabelText = styled.span`
	font-weight: 600;
`;

const StyledCheckbox = styled(Form.Check)`
	.form-check-input {
		border: 2px solid ${props => props.color};
		&:checked {
			border-color: ${props => props.color};
			background-color: ${props => props.color};
		}
	}
`;

const ANNOTATION_SIDEBAR_ID = 'annotationSidebar';

// antd's Drawer has no corner radius. The panel and the wrapper that carry its
// shadow take the theme's large radius, so the corners and the shadow both round.
// The header and body spacing sits here too: a `padding` key in the Drawer's
// `styles` prop is what the design gate bans. `display: contents` keeps the
// wrapper out of the layout: the drawer still sits in the map area.
const RoundedDrawerFrame = styled.div`
	display: contents;

	.ant-drawer-content-wrapper,
	.ant-drawer-section {
		border-radius: ${({ theme }) => theme.token.borderRadiusLG}px;
	}

	.ant-drawer-header {
		padding: ${({ theme }) => theme.token.paddingSM}px;
		border-bottom: ${({ theme }) => theme.token.lineWidth}px
			${({ theme }) => theme.token.lineType} ${({ theme }) => theme.token.colorBorder};
	}

	.ant-drawer-body {
		padding: ${({ theme }) => theme.token.paddingSM}px;
	}
`;
// The width the map sidebars have always had.
const DRAWER_WIDTH = 360;

/**
 * The object tree of a Collection view object session, in an antd Drawer on the
 * left of the map. It renders inside the map area (`getContainer={false}`) with
 * no mask, so the map stays usable: only the panel takes pointer events. It
 * stops above the bottom toolbar, and its body scrolls on its own.
 */
const ObjectTreeDrawer = ({ header }) => {
	const { activeSidebars } = useProject();
	const { token } = theme.useToken();
	const open = activeSidebars.some(sidebar => sidebar.sidebarId === ANNOTATION_SIDEBAR_ID);

	return (
		<RoundedDrawerFrame>
			<Drawer
				open={open}
				title={header}
				placement="left"
				size={DRAWER_WIDTH}
				getContainer={false}
				mask={false}
				closable={false}
				// Esc belongs to the session (sketch, selection, exit), and the keys to the map.
				keyboard={false}
				focusable={{ trap: false, focusTriggerAfterClose: false }}
				zIndex={1}
				rootStyle={{
					top: token.marginXXS,
					left: token.marginXXS,
					// Clear of the bottom toolbar, as the map sidebars are.
					bottom: token.controlHeightLG + token.marginLG,
				}}>
				<AnnotationObjectTree />
			</Drawer>
		</RoundedDrawerFrame>
	);
};

/**
 * The sidebar of annotation mode: in a Collection view object session the object
 * tree, in a Drawer; in Model view the model's classes, in the map sidebar — the
 * draw tools still key boxes by class.
 */
const AnnotationSidebar = () => {
	const {
		annotationSidebarData: sidebarData,
		annotationMode,
		colorOptions,
		dispatch,
	} = useProject();
	const showClasses = useProjectMode() === PROJECT_VIEW_MODE.MODEL;
	// The object tree belongs to the Collection view flow only; a model session keeps
	// its class radios and nothing else.
	const isObjectSession = annotationMode?.target === ANNOTATION_TARGET.OBJECT;

	// Model view keeps its wording; the object flow names its phase.
	const HEADER_BY_PHASE = {
		[ANNOTATION_PHASE.PICK]: 'Pick an object',
		[ANNOTATION_PHASE.ANNOTATE]: 'Annotating',
	};
	const header =
		(isObjectSession && HEADER_BY_PHASE[annotationMode?.phase]) || 'Items to annotate';

	const { classIds, labels, activeClassId } = sidebarData || {
		classIds: [],
		labels: [],
	};

	const handleClick = classId => {
		dispatch({
			type: 'setAnnotationSidebarData',
			payload: {
				classIds,
				labels,
				activeClassId: classId,
			},
		});
	};

	if (isObjectSession) return <ObjectTreeDrawer header={header} />;

	const Label = ({ text, keyboardShortcutText }) => {
		return (
			<span
				data-tooltip-id="annotate-category-tip"
				data-tooltip-content={`Shortcut key: ${keyboardShortcutText}`}
				data-tooltip-place="top">
				<LabelText>{text}</LabelText>{' '}
				<KeyboardShortcut>{keyboardShortcutText}</KeyboardShortcut>
			</span>
		);
	};

	return (
		<SidebarTemplate
			header={header}
			zindex={1}
			sidebarId="annotationSidebar"
			closeable={false}
			align="bottom">
			{showClasses && (
				<FilterTab>
					<Form>
						{classIds.map((classId, index) => {
							return (
								<Radio
									key={classId}
									type="radio"
									classId={classId}
									label={
										<Label
											text={labels[index]}
											keyboardShortcutText={classId + 1}
										/>
									}
									name="annotation"
									id={`annotate-radio-${classId}`}
									handleClick={handleClick}
									checked={activeClassId === classId}
									color={colorOptions[classId % Object.keys(colorOptions).length]}
								/>
							);
						})}
						<Tooltip id="annotate-category-tip" variant="light" />
					</Form>
				</FilterTab>
			)}
		</SidebarTemplate>
	);
};
export default AnnotationSidebar;

const Radio = ({ classId, handleClick, color, ...props }) => {
	// Keyboard shortcuts for radio buttons
	useKeyboardShortcut(classId + 1, true, () => {
		handleClick(classId);
	});

	const { r, g, b } = color;

	return (
		<StyledCheckbox
			onChange={() => {
				handleClick(classId);
			}}
			color={`rgb(${r}, ${g}, ${b})`}
			{...props}
		/>
	);
};
